import React, { useState, useRef } from "react";
import {
  Button,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Slide,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { UploadFile, Download } from "@mui/icons-material";
import { motion } from "framer-motion";

const AnimatedButton = motion(Button);
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const UploadDownloadCard = ({ sessionID }) => {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const fileInputRef = useRef();

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleUploadClick = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setOpen(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    event.target.value = null;
  };

  const handleUploadConfirm = async () => {
    if (!selectedFile) {
      showSnackbar("No file selected", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    setUploading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/update-company-status-excel`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Upload failed");
      }

      const result = await response.json();
      showSnackbar(`Upload successful: ${result.message || selectedFile.name}`, "success");
    } catch (error) {
      showSnackbar(`Upload failed: ${error.message}`, "error");
    } finally {
      setUploading(false);
      setOpen(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

const handleDownload = async () => {
  const downloadUrl = `${process.env.REACT_APP_API_BASE_URL}/download-excel?session_id=${sessionID}`;

  try {
    const response = await fetch(downloadUrl);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Download failed");
    }

    const blob = await response.blob();
    if (blob.size === 0) {
      throw new Error("Empty file received");
    }

    // ✅ Extract filename from Content-Disposition
    const disposition = response.headers.get("Content-Disposition");
    let filename = "report.xlsx";
    debugger;

    if (disposition && disposition.includes("filename=")) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) {
        filename = decodeURIComponent(match[1]);
      }
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    showSnackbar("Download successful!", "success");
  } catch (error) {
    showSnackbar(`Download failed: ${error.message}`, "error");
  }
};



  return (
    <>
      <Box
        display="flex"
        gap={2}
        p={2}
        justifyContent="center"
        alignItems="center"
        sx={{
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(255,255,255,0.1)",
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          width: "92%",
          margin: "auto",
          mb: 2,
        }}
      >
        <Tooltip title="Upload a file" arrow>
          <AnimatedButton
            variant="outlined"
            startIcon={<UploadFile />}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleUploadClick}
            sx={{
              borderRadius: 5,
              textTransform: "none",
              px: 2,
              py: 1,
              borderColor: "#1C2758",
              color: "#1C2758",
              "&:hover": {
                background: "#f0f4ff",
                borderColor: "#1C2758",
              },
            }}
          >
            Upload
          </AnimatedButton>
        </Tooltip>

        <Tooltip title="Download the file" arrow>
          <AnimatedButton
            variant="outlined"
            startIcon={<Download />}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleDownload}
            sx={{
              borderRadius: 5,
              textTransform: "none",
              px: 2,
              py: 1,
              borderColor: "#08AA66",
              color: "#08AA66",
              "&:hover": {
                background: "#e0f2f1",
                borderColor: "#067e4d",
              },
            }}
          >
            Download
          </AnimatedButton>
        </Tooltip>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        keepMounted
        BackdropProps={{
          sx: {
            backgroundColor: "rgb(0 0 0 / 9%)",
            backdropFilter: "blur(7px)",
          },
        }}
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, y: 0 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0 },
          transition: { delay: 0.1, duration: 0.1 },
          sx: {
            background: "rgba(255, 255, 255, 0.4)",
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
            minWidth: "30%",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#1C2758" }}>
          Upload File
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#1C2758" sx={{ mb: 1 }}>
            Please choose a file to upload:
          </Typography>
          <Box display="flex" flexDirection="column" alignItems="start">
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFile />}
              sx={{
                borderRadius: 5,
                textTransform: "none",
                px: 2,
                py: 1,
                borderColor: "#1C2758",
                color: "#1C2758",
                "&:hover": {
                  background: "#f0f4ff",
                  borderColor: "#1C2758",
                },
              }}
            >
              Choose File
              <input
                type="file"
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </Button>
            {selectedFile && (
              <Typography variant="caption" mt={1} color="#1C2758">
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadConfirm}
            disabled={!selectedFile || uploading}
            sx={{ color: "#08AA66", fontWeight: 600, minWidth: 90 }}
            startIcon={
              uploading ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Custom Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            backgroundColor: "rgba(0, 123, 255, 0.1)",
            color: "#0d47a1",
            backdropFilter: "blur(5px)",
            borderRadius: 3,
            border: "1px solid #90caf9",
            fontWeight: 500,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UploadDownloadCard;
